import { Box, Collapse, FormHelperText } from '@mui/material';
import Dropzone from 'components/common/dropzone/Dropzone';
import LightboxModal from 'components/common/modal/LightboxModal';
import { AUDIO_TYPES, IMAGE_TYPES, VIDEO_TYPES } from 'components/utils/constants';
import RenderThumbnails from 'pages/companyFiles/components/RenderThumbnails';
import { ArticleType, AttachmentType } from 'pages/knowledge/hooks/useArticlesQuery';
import { useMemo, useState } from 'react';
import { Accept } from 'react-dropzone';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getExactFileName } from 'utility/helpers';
import { ActivityFormValues } from './ActivityForm';

type AttachmentsProps = {
  accept?: Accept;
  loadingFiles: boolean;
  onDrop: (acceptedFiles: File[]) => void;
};

function Attachments({ accept, loadingFiles, onDrop }: AttachmentsProps) {
  const {
    control,
    formState: { isSubmitting },
    getFieldState,
    setValue,
  } = useFormContext<ActivityFormValues>();
  const fieldState = getFieldState('attachments');
  const [lightboxOpen, setLightboxOpen] = useState<{
    open: boolean;
    fileName: string;
  }>({
    open: false,
    fileName: '',
  });
  const attachments = useWatch({
    control,
    name: 'attachments',
    defaultValue: [],
  });
  const [mediaFiles, thumbnails] = useMemo(() => {
    const mediaFiles: AttachmentType[] = [];
    const thumbnails: AttachmentType[] = [];

    attachments?.forEach((attachment) => {
      let fileType = `${attachment.fileType.toLowerCase().split('/')?.[1]}`;
      if (attachment.fileUploaded) {
        fileType = attachment.fileType.toLowerCase();
      }

      const isMediaFile =
        IMAGE_TYPES.includes(`image/${fileType}`) ||
        VIDEO_TYPES.includes(`video/${fileType}`) ||
        AUDIO_TYPES.includes(`audio/${fileType}`);

      if (isMediaFile && !attachment?.isRemoved) {
        mediaFiles.push(attachment);
      }

      if (!attachment?.isRemoved) {
        thumbnails.push(attachment);
      }
    });

    return [mediaFiles, thumbnails];
  }, [attachments]);

  const onRemoveFile = (file: AttachmentType) => {
    const fileName = getExactFileName(file);

    const updatedAttachments = [...attachments];
    const index = updatedAttachments.findIndex(
      (attachment) => attachment.fileName.toLowerCase() === file.fileName.toLowerCase(),
    );
    if (file.fileUploaded) {
      updatedAttachments[index] = {
        ...updatedAttachments[index],
        isRemoved: true,
      };
    } else {
      updatedAttachments.splice(index, 1);
    }

    setValue('attachments', updatedAttachments, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateAttachment = (fileName: string, updatedAttachment: AttachmentType) => {
    const updatedAttachments = [...attachments];
    const index = updatedAttachments.findIndex(
      (file) => file.fileName.toLowerCase() === fileName.toLowerCase(),
    );

    if (updatedAttachment.fileUploaded) {
      updatedAttachments[index] = {
        ...updatedAttachment,
        isUpdated: true,
      };
    } else {
      updatedAttachments[index] = updatedAttachment;
    }

    setValue('attachments', updatedAttachments, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Box mt={1}>
      <Controller
        render={({ field: { ref } }) => (
          <Box ref={ref} mb={1}>
            <Dropzone
              accept={accept}
              onDrop={onDrop}
              loadingFiles={loadingFiles}
              multiple
              disabled={isSubmitting}
            />
          </Box>
        )}
        name="attachments"
      />
      {!!fieldState?.error && (
        <FormHelperText sx={{ ml: 0, '&::first-letter': { textTransform: 'uppercase' } }} error>
          {fieldState?.error?.message}
        </FormHelperText>
      )}
      <Collapse in={thumbnails.length > 0} aria-disabled={isSubmitting} sx={{ mb: 2 }}>
        <RenderThumbnails
          defaultGridMdSize={12}
          files={thumbnails}
          removeFile={onRemoveFile}
          setLightboxOpen={setLightboxOpen}
          disabled={isSubmitting}
          showEdit
          showEditLogoOnly
          isImage={(file: AttachmentType) => {
            let fileType = `${file.fileType.toLowerCase().split('/')?.[1]}`;
            if (file.fileUploaded) {
              fileType = file.fileType.toLowerCase();
            }
            return IMAGE_TYPES.includes(
              file.fileUploaded ? `image/${fileType}` : file.fileType.toLowerCase(),
            );
          }}
        />
      </Collapse>

      {lightboxOpen.open && (
        <LightboxModal<ArticleType>
          onClose={() => setLightboxOpen({ open: false, fileName: '' })}
          open={lightboxOpen.open}
          mode="edit"
          mediaFiles={mediaFiles}
          defaultOpenFileName={lightboxOpen.fileName}
          updateFile={updateAttachment}
        />
      )}
    </Box>
  );
}

export default Attachments;
